import { Select } from '@nimbus-ds/components'
import { useQuery } from '@tanstack/react-query'
import { storeService } from '@/api/store'
import { authService } from '@/api/auth'
import { storeDisplayName } from '@/lib/storeName'

/**
 * Store picker for merchants holding more than one membership — an agency doing
 * acquisition across several stores, say.
 *
 * Renders NOTHING for the single-store merchant, which is the common case: the
 * header stays as lean as the 0.2 handoff intends, and nobody is offered a
 * choice they don't have.
 *
 * Options come from the TOKEN's memberships, never from the store list: the API
 * supplies display names, but what she may switch to is decided by the JWT.
 */
export function MerchantStoreSwitcher() {
  const memberships = authService.getUserInfo()?.memberships ?? []
  const currentStoreId = authService.getCurrentStoreId()
  const multiStore = memberships.length > 1

  // Shares the consultant switcher's cache key — same data, fetched once.
  const { data: stores = [] } = useQuery({
    queryKey: ['user-stores'],
    queryFn: () => storeService.getAllStores(),
    staleTime: 5 * 60 * 1000,
    enabled: multiStore,
  })

  // Uninstalled stores are not offered; the current one stays so the control never goes blank.
  const uninstalled = new Set(stores.filter((s) => s.uninstalled_at).map((s) => String(s.id)))
  const options = memberships.filter((m) => String(m.store_id) === currentStoreId || !uninstalled.has(String(m.store_id)))

  if (!multiStore || options.length <= 1) return null

  // Falls back to the id so the control is usable on first paint, before the
  // store list resolves, instead of showing blank options.
  const label = (storeId: string) => {
    const match = stores.find((s: any) => String(s.id) === storeId)
    const name = (match && storeDisplayName((match as any).name)) || `Tienda ${storeId}`
    return match?.uninstalled_at ? `${name} · desinstalada` : name
  }

  // Capped so a long store name can't crowd out the page title next to it —
  // this header is mobile-first and the title is the more important half.
  return (
    <div className="max-w-[9rem] md:max-w-[14rem]">
      <Select
        id="merchant-store-switcher"
        name="merchant-store-switcher"
        aria-label="Cambiar de tienda"
        value={currentStoreId || ''}
        onChange={(e) => authService.switchStore(e.target.value)}
      >
        {options.map((m) => {
          const id = String(m.store_id)
          return <Select.Option key={id} value={id} label={label(id)} />
        })}
      </Select>
    </div>
  )
}
